/**
 * #3991 (F7): how should a failed subscription-pricing load be handled?
 *
 * There are two separate questions here, and the first version of this file collapsed them into
 * one, which produced a lie in both directions.
 *
 *   1. Do we REPORT the error? Being offline is not an app bug, so a plain "could not reach the
 *      server" while offline must not go to Sentry. But "we happened to be offline when it threw"
 *      is NOT the same claim as "the throw was caused by being offline". An error we cannot
 *      explain still gets reported, even offline — otherwise a genuine defect in the pricing code
 *      would be invisible forever, because offline is exactly when this path runs.
 *
 *   2. Do we TELL THE USER they are offline? Only when we actually believe they are. A transient
 *      fetch error is NOT proof of that: uBlock, a corporate proxy, or a CORS misconfiguration all
 *      make `fetch` throw `TypeError: Failed to fetch` on a perfectly good connection. Telling
 *      that user "You're Offline" sends them to check their wifi forever while the real cause sits
 *      in an extension — and, worse, the first version also swallowed the report, so we would
 *      never have found out. On a revenue path.
 *
 * So the error's own shape decides whether a RETRY is worth offering, and our connectivity belief
 * decides what we CLAIM. They are answered independently below.
 */
export enum PricingFailureKind {
  /** The error says "could not reach the server", and we do believe we are offline. Expected, not
   *  a bug: show the offline alert, do NOT report it. */
  OfflineConnectivity = 'offline-connectivity',
  /** The error says "could not reach the server", but we believe we are ONLINE. Something between
   *  us and the server is eating the request (ad blocker, proxy, CORS, a flaky network the probe
   *  has not noticed yet). Offer the same in-place Try Again — a reload would not help and a retry
   *  might — but do NOT claim they are offline, and DO report it: this is the shape a blocked
   *  request takes, and we want to see how often it happens. */
  TransientWhileOnline = 'transient-while-online',
  /** An error we cannot explain, thrown while offline. The USER's experience is the same as a
   *  connectivity failure (and a reload would not help them either), so show them the same offline
   *  alert — but REPORT it, because we genuinely do not know what it was. */
  UnknownWhileOffline = 'unknown-while-offline',
  /** An error we cannot explain, thrown while online. A real failure: report it and offer the
   *  original reload alert. This is the pre-#3991 behavior, unchanged. */
  AppError = 'app-error',
}

export function classifyPricingFailure(input: {
  /** Did the error's own shape say "could not reach the server"? (isTransientFetchError) */
  isTransientFetchError: boolean;
  /** Was it our own 30s timeout? */
  isTimeout: boolean;
  /** Do we believe the device is offline? Decides what we CLAIM, never whether we report. */
  isOffline: boolean;
}): PricingFailureKind {
  const looksLikeNetwork = input.isTransientFetchError || input.isTimeout;
  if (looksLikeNetwork) {
    return input.isOffline ? PricingFailureKind.OfflineConnectivity : PricingFailureKind.TransientWhileOnline;
  }
  return input.isOffline ? PricingFailureKind.UnknownWhileOffline : PricingFailureKind.AppError;
}

/** Do we tell the user they are offline? Only when we actually believe it. */
export function claimsOffline(kind: PricingFailureKind): boolean {
  return kind === PricingFailureKind.OfflineConnectivity || kind === PricingFailureKind.UnknownWhileOffline;
}

/** Do we offer an in-place Try Again (rather than the old reload-the-app alert)? Everything except
 *  a genuine app error is worth retrying in place — a reload would only lose the user's context. */
export function offersInPlaceRetry(kind: PricingFailureKind): boolean {
  return kind !== PricingFailureKind.AppError;
}

/** Must this outcome reach a future debugger? Only a plain connectivity failure that we can fully
 *  explain — network error, and we know we are offline — stays silent. */
export function mustReport(kind: PricingFailureKind): boolean {
  return kind !== PricingFailureKind.OfflineConnectivity;
}
